// Firebase Tag Service
// Handles CRUD operations for tags in Firebase

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  increment,
  DocumentData,
} from "firebase/firestore";
import { db } from "../../../utils/firebase";
import { OutreachCategory, Tag } from "../../../types/outreach";
import { convertDoc } from "../utils/helpers";

// Tag creation interface (without auto-generated fields)
export interface CreateTagRequest {
  name: string;
  description?: string;
  category: OutreachCategory | "global";
  color?: string;
  createdBy?: string;
}

// Tag search/filter options
export interface TagSearchOptions {
  category?: OutreachCategory | "global";
  searchTerm?: string;
  limit?: number;
  includeGlobal?: boolean; // When searching by category, also include global tags
}

const TAGS_COLLECTION_PATH = "outreach/data/outreach-tags";

/**
 * Get all tags or filter by options
 */
export const getTags = async (
  options: TagSearchOptions = {}
): Promise<Tag[]> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const tagsRef = collection(db, TAGS_COLLECTION_PATH);
    let q = query(tagsRef, orderBy("name"));

    // Apply category filter
    if (options.category) {
      if (options.includeGlobal && options.category !== "global") {
        // This would require a compound query or multiple queries
        // For now, we'll fetch all and filter in memory
        const snapshot = await getDocs(q);
        const allTags = snapshot.docs.map((doc) => convertDoc<Tag>(doc));

        return allTags.filter(
          (tag) =>
            tag.category === options.category || tag.category === "global"
        );
      } else {
        q = query(
          tagsRef,
          where("category", "==", options.category),
          orderBy("name")
        );
      }
    }

    // Apply limit
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    const snapshot = await getDocs(q);
    let tags = snapshot.docs.map((doc) => convertDoc<Tag>(doc));

    // Apply search term filter (client-side for now)
    if (options.searchTerm && options.searchTerm.trim()) {
      const searchLower = options.searchTerm.toLowerCase();
      tags = tags.filter(
        (tag) =>
          tag.name.toLowerCase().includes(searchLower) ||
          tag.description?.toLowerCase().includes(searchLower)
      );
    }

    return tags;
  } catch (error) {
    console.error("Error getting tags:", error);
    return [];
  }
};

/**
 * Get tags for autocomplete with search
 */
export const searchTags = async (
  searchTerm: string,
  category?: OutreachCategory | "global",
  limitResults: number = 10
): Promise<Tag[]> => {
  return getTags({
    searchTerm,
    category,
    limit: limitResults,
    includeGlobal: category !== undefined && category !== "global",
  });
};

/**
 * Get a single tag by ID
 */
export const getTag = async (id: string): Promise<Tag | null> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const tagDoc = await getDoc(doc(db, TAGS_COLLECTION_PATH, id));
    if (tagDoc.exists()) {
      return { id: tagDoc.id, ...tagDoc.data() } as Tag;
    }
    return null;
  } catch (error) {
    console.error(`Error getting tag ${id}:`, error);
    return null;
  }
};

/**
 * Create a new tag
 */
export const createTag = async (tagData: CreateTagRequest): Promise<string> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    // Check if tag with same name already exists in the category
    const existingTags = await getTags({
      category: tagData.category,
      searchTerm: tagData.name,
    });

    const exactMatch = existingTags.find(
      (tag) => tag.name.toLowerCase() === tagData.name.toLowerCase()
    );

    if (exactMatch) {
      throw new Error(`Tag "${tagData.name}" already exists in this category`);
    }

    const newTag: Omit<Tag, "id"> = {
      ...tagData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      usageCount: 0,
    };

    const docRef = await addDoc(collection(db, TAGS_COLLECTION_PATH), newTag);
    return docRef.id;
  } catch (error) {
    console.error("Error creating tag:", error);
    throw error;
  }
};

/**
 * Update an existing tag
 */
export const updateTag = async (
  id: string,
  updates: Partial<CreateTagRequest>
): Promise<void> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    await updateDoc(
      doc(db, TAGS_COLLECTION_PATH, id),
      updateData as DocumentData
    );
  } catch (error) {
    console.error(`Error updating tag ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a tag
 */
export const deleteTag = async (id: string): Promise<void> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    await deleteDoc(doc(db, TAGS_COLLECTION_PATH, id));
  } catch (error) {
    console.error(`Error deleting tag ${id}:`, error);
    throw error;
  }
};

/**
 * Increment tag usage count when it's used
 */
export const incrementTagUsage = async (id: string): Promise<void> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastModified: new Date() }, { merge: true });

    await updateDoc(doc(db, TAGS_COLLECTION_PATH, id), {
      usageCount: increment(1),
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error(`Error incrementing tag usage ${id}:`, error);
    // Don't throw here as this is not critical
  }
};

/**
 * Get popular tags by usage count
 */
export const getPopularTags = async (
  category?: OutreachCategory | "global",
  limitResults: number = 10
): Promise<Tag[]> => {
  try {
    // Ensure the "data" document exists
    const dataDocRef = doc(db, "outreach", "data");
    await setDoc(dataDocRef, { lastAccessed: new Date() }, { merge: true });

    const tagsRef = collection(db, TAGS_COLLECTION_PATH);
    let q = query(tagsRef, orderBy("usageCount", "desc"), limit(limitResults));

    if (category) {
      q = query(
        tagsRef,
        where("category", "==", category),
        orderBy("usageCount", "desc"),
        limit(limitResults)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => convertDoc<Tag>(doc));
  } catch (error) {
    console.error("Error getting popular tags:", error);
    return [];
  }
};
